import { ChangeEvent, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { TiTrash } from 'react-icons/ti';
import Swal from 'sweetalert2';

import {
  DataTable,
  StyledButton,
  StyledInput,
  StyledSelect,
} from '../../../../components';
import { Loading } from '../../../../components/Loading';
import { TableActionButton } from '../../../../components/TableActionButton';
import {
  deletePlano,
  Plano,
  PlanId,
  savePlano,
  usePlanos,
} from '../../../../services/hooks/usePlanos';
import { DocumentMask } from '../../../../utils/DocumentMask';
import { documentValidation } from '../../../../utils/DocumentValidation';
import { AdminTitle } from '../../components';
import { Form, PlanosContainer, Row } from './styles';

const tableHead = ['CPF/CNPJ', 'Plano', 'Ações'];

const planoOptions = [
  { id: 'ZIG15', value: 'ZIG15' },
  { id: 'ZIG30', value: 'ZIG30' },
  { id: 'ZIG70', value: 'ZIG70' },
];

export const Planos = () => {
  const { data, isLoading, refetch } = usePlanos();

  const [documento, setDocumento] = useState('');
  const [plano, setPlano] = useState<PlanId>('ZIG30');
  const [saving, setSaving] = useState(false);
  const [showError, setShowError] = useState(false);

  const validation = useMemo(
    () => documentValidation(documento),
    [documento]
  );

  const handleDocumento = (event: ChangeEvent<HTMLInputElement>) => {
    setDocumento(event.target.value.replace(/\D/g, '').slice(0, 14));
    setShowError(false);
  };

  const handleSave = async () => {
    if (!validation.valid) {
      setShowError(true);
      return;
    }

    setSaving(true);
    try {
      await savePlano(documento, plano);
      setDocumento('');
      setPlano('ZIG30');
      setShowError(false);
      refetch();
      Swal.fire({
        title: 'Sucesso!',
        html: `Plano ${plano} salvo para ${DocumentMask(documento)}.`,
        confirmButtonColor: '#F58220',
      });
    } catch (error: any) {
      const message =
        error.response?.data?.message ?? 'Erro ao salvar o plano';
      Swal.fire({
        title: 'Erro - Tente novamente!',
        html: message,
        confirmButtonColor: '#F58220',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: Plano) => {
    Swal.fire({
      title: 'Remover plano',
      html: `Remover o plano de ${DocumentMask(
        item.document
      )}? Ele voltará ao padrão (ZIG30).`,
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#F58220',
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setSaving(true);
      try {
        await deletePlano(item.id);
        refetch();
      } catch (error: any) {
        const message =
          error.response?.data?.message ?? 'Erro ao remover o plano';
        Swal.fire({
          title: 'Erro - Tente novamente!',
          html: message,
          confirmButtonColor: '#F58220',
        });
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <>
      {(isLoading || saving) && <Loading />}
      <PlanosContainer>
        <AdminTitle title="Planos por CPF/CNPJ" icon={<FiPlus />} />

        <p className="hint">
          Cadastre o plano de taxas por CPF ou CNPJ. Documentos sem cadastro
          usam o plano padrão <strong>ZIG30</strong>.
        </p>

        <Form>
          <div className="field documento">
            <label htmlFor="plano-documento">CPF/CNPJ</label>
            <StyledInput
              id="plano-documento"
              inputMode="numeric"
              placeholder="CPF ou CNPJ"
              value={DocumentMask(documento)}
              onChange={handleDocumento}
              onBlur={() => setShowError(documento !== '')}
              error={showError && !validation.valid}
              errorMessage={validation.message}
            />
          </div>

          <div className="field plano">
            <label htmlFor="plano-plano">Plano</label>
            <StyledSelect
              options={planoOptions}
              value={plano}
              setValue={(value) => setPlano(value as PlanId)}
            />
          </div>

          <StyledButton
            icon={<FiPlus />}
            iconPosition="left"
            text="Salvar"
            onClick={handleSave}
          />
        </Form>

        <DataTable title="Planos cadastrados" heads={tableHead} hasPagination={false}>
          <tbody>
            {data && data.length > 0 ? (
              data.map((item) => (
                <Row key={item.id}>
                  <td>{DocumentMask(item.document)}</td>
                  <td>{item.plan}</td>
                  <td>
                    <TableActionButton
                      label="Excluir plano"
                      icon={<TiTrash />}
                      onClick={() => handleDelete(item)}
                    />
                  </td>
                </Row>
              ))
            ) : (
              <Row>
                <td colSpan={3}>Nenhum plano cadastrado.</td>
              </Row>
            )}
          </tbody>
        </DataTable>
      </PlanosContainer>
    </>
  );
};
